import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useTrialGuard from '../hooks/useTrialGuard';
import TrialExpiredModal from './ui/TrialExpiredModal';
import { canAccessRoute } from '../utils/rbac';

/**
 * ProtectedRoute - Checks authentication, license tier, and optional role restrictions.
 * superadminOnly: if true, only superadmin users can access; org_admin and below are redirected.
 * adminOnly: if true, only org_admin+ users can access; non-admins are redirected to /access-denied.
 * Also enforces trial expiry and license-based menu access.
 *
 * Expired subscription behaviour:
 * - Non-admin members: redirected to /subscription-billing (their accounts are set inactive by useTrialGuard)
 * - Org Admin/Owner: remain active but are restricted to /dashboard, /organization-settings,
 *   and /subscription-billing. Any other route redirects them to /subscription-billing.
 */
const ProtectedRoute = ({ children, requiredRole = null, screen = null, superadminOnly = false, adminOnly = false }) => {
  const { user, loading, profileLoading, userProfile, isSuperAdmin, isOrgAdmin } = useAuth();
  const location = useLocation();
  const [profileTimedOut, setProfileTimedOut] = useState(false);

  // Trial guard — runs for all authenticated users; superadmins are exempt inside the hook
  const { blocked: trialBlocked, blockedAsNonAdmin, blockedAsAdmin, loading: trialLoading, onActivated } = useTrialGuard();

  // Safety valve: if profileLoading is stuck for more than 8 seconds, unblock the route
  useEffect(() => {
    if (!profileLoading) {
      setProfileTimedOut(false);
      return;
    }
    const timer = setTimeout(() => {
      setProfileTimedOut(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [profileLoading]);

  // Wait for auth to finish loading first
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If no user after loading completes, redirect to login
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // User is authenticated — wait for profile to load (but not forever)
  if (profileLoading && !profileTimedOut) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Superadmin-only pages: redirect org_admin and other non-superadmin roles
  if (superadminOnly && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Admin-only pages: redirect non-admins (org_member and below) to access-denied
  if (adminOnly && !isOrgAdmin) {
    return <Navigate to="/access-denied" replace state={{ from: location?.pathname }} />;
  }

  // Trial guard loading — wait for all users (not just org_admins)
  if (trialLoading && !isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Non-admin users whose org subscription is expired/suspended: redirect to subscription-billing
  if (blockedAsNonAdmin) {
    return <Navigate to="/subscription-billing" replace />;
  }

  // Org-admin/owner users whose subscription is expired and are on a restricted route:
  // redirect to subscription-billing (they can still access dashboard, org-settings, and billing)
  if (blockedAsAdmin) {
    return <Navigate to="/subscription-billing" replace />;
  }

  // Org-admin users whose trial is expired and are on an allowed path: show the TrialExpiredModal
  // as an overlay so they can still see the page underneath (dashboard or billing)
  const isOrgAdminRole = userProfile?.system_role === 'org_admin';
  if (trialBlocked && isOrgAdminRole) {
    return (
      <>
        {children}
        <TrialExpiredModal isOpen={true} onActivated={onActivated} />
      </>
    );
  }

  // License-based route access check (skip for superadmin)
  if (userProfile && !isSuperAdmin) {
    const routePath = location?.pathname;
    if (!canAccessRoute(userProfile, routePath)) {
      // Redirect to the highest-access page available for their license
      const license = userProfile?.license_type;
      const redirectTo = license === 'basic' ? '/ticket-management' : '/my-dashboard';
      return <Navigate to={redirectTo} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
